import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus } from "lucide-react";
/**
 * Gestión de series de comprobantes — admin del tenant.
 * Solo usuarios con permiso `comprobante:anular` o `comprobante:create`.
 */
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { formatApiError } from "../api/client";
import {
  type Serie,
  type SerieCreatePayload,
  type TipoComprobante,
  createSerie,
  listSeries,
  toggleSerie,
} from "../api/facturacion";
import { type Sede, listSedes } from "../api/tenants";
import { useAuth } from "../auth/AuthProvider";
import { useBase } from "../auth/TenantBrandingProvider";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

export function SeriesPage() {
  const base = useBase();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [tipo, setTipo] = useState<TipoComprobante>("BOLETA");
  const [serieValor, setSerieValor] = useState("B001");
  const [sedeId, setSedeId] = useState<number | null>(null);
  const [correlativoInicial, setCorrelativoInicial] = useState("0");
  const [error, setError] = useState<string | null>(null);

  const sedesQuery = useQuery({ queryKey: ["sedes"], queryFn: listSedes });
  const seriesQuery = useQuery({ queryKey: ["series"], queryFn: () => listSeries() });

  const series: Serie[] = seriesQuery.data ?? [];
  const sedes: Sede[] = sedesQuery.data ?? [];
  const sedeNombre = (id: number) => sedes.find((s) => s.id === id)?.nombre ?? "—";

  const createMut = useMutation({
    mutationFn: (payload: SerieCreatePayload) => createSerie(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["series"] });
      setCreateOpen(false);
      setError(null);
      setSerieValor("B001");
      setCorrelativoInicial("0");
    },
    onError: (err) => setError(formatApiError(err, "No se pudo crear la serie.")),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, activa }: { id: number; activa: boolean }) => toggleSerie(id, activa),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["series"] }),
  });

  if (!me?.permisos.includes("comprobante:create")) {
    return <Navigate to={`${base}/facturacion`} replace />;
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!sedeId) return setError("Seleccioná una sede.");
    if (!/^[BF][0-9]{3}$/.test(serieValor)) {
      return setError("La serie debe ser de 4 caracteres: B### o F###.");
    }
    const prefijoEsperado = tipo === "BOLETA" ? "B" : "F";
    if (serieValor[0] !== prefijoEsperado) {
      return setError(`${tipo} debe usar serie que empieza con '${prefijoEsperado}'.`);
    }
    createMut.mutate({
      sede_id: sedeId,
      tipo_comprobante: tipo,
      serie: serieValor,
      correlativo_inicial: Number(correlativoInicial) || 0,
    });
  }

  return (
    <div className="space-y-6">
      <Link
        to={`${base}/facturacion`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a facturación
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Series</h1>
          <p className="text-sm text-slate-500 mt-1">
            Cada sede maneja sus propias series. Una serie inactiva impide emitir nuevos
            comprobantes (los históricos no se ven afectados).
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4" />
          Nueva serie
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="text-sm text-slate-600">{series.length} series configuradas</div>
        </CardHeader>
        <CardBody className="p-0">
          {seriesQuery.isLoading ? (
            <div className="p-6 text-sm text-slate-500">Cargando…</div>
          ) : series.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">
              No hay series. Crea una serie B### (boletas) y otra F### (facturas) por cada sede que
              emita comprobantes.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Serie</th>
                  <th className="text-left px-4 py-2 font-medium">Tipo</th>
                  <th className="text-left px-4 py-2 font-medium">Sede</th>
                  <th className="text-right px-4 py-2 font-medium">Correl.</th>
                  <th className="text-center px-4 py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {series.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-mono">{s.serie}</td>
                    <td className="px-4 py-2 text-slate-700">{s.tipo_comprobante}</td>
                    <td className="px-4 py-2 text-slate-700">{sedeNombre(s.sede_id)}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{s.correlativo_actual}</td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleMut.mutate({ id: s.id, activa: !s.activa })}
                        className="inline-flex items-center"
                        aria-label={s.activa ? "Desactivar" : "Activar"}
                        disabled={toggleMut.isPending}
                      >
                        <Badge variant={s.activa ? "success" : "neutral"}>
                          {s.activa ? "Activa" : "Inactiva"}
                        </Badge>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setError(null);
        }}
        title="Nueva serie"
        description="El correlativo arranca en 0. La primera emisión tendrá correlativo 1."
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Field label="Tipo" htmlFor="tipo-serie">
            <select
              id="tipo-serie"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              value={tipo}
              onChange={(e) => {
                const t = e.target.value as TipoComprobante;
                setTipo(t);
                setSerieValor(t === "BOLETA" ? "B001" : "F001");
              }}
            >
              <option value="BOLETA">Boleta</option>
              <option value="FACTURA">Factura</option>
            </select>
          </Field>
          <Field
            label="Serie"
            htmlFor="serie-valor"
            hint="Formato SUNAT: B### para boleta, F### para factura."
          >
            <Input
              id="serie-valor"
              value={serieValor}
              onChange={(e) => setSerieValor(e.target.value.toUpperCase())}
              maxLength={4}
            />
          </Field>
          <Field label="Sede" htmlFor="sede-serie">
            <select
              id="sede-serie"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              value={sedeId ?? ""}
              onChange={(e) => setSedeId(Number(e.target.value) || null)}
            >
              <option value="">— Seleccionar —</option>
              {sedes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Correlativo inicial" htmlFor="correl-inicial">
            <Input
              id="correl-inicial"
              type="number"
              min="0"
              value={correlativoInicial}
              onChange={(e) => setCorrelativoInicial(e.target.value)}
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCreateOpen(false);
                setError(null);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={createMut.isPending}>
              Crear serie
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
